import { apiClient } from './api';
import type { CommunicationPlan } from '../components/executive-protection/CommunicationPlanEditor';
import { toUtcIso } from '@/utils/datetime';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

import {
  type ProtectedPerson as BaseProtectedPerson,
  type BackendProtectedPerson,
  transformProtectedPerson,
  transformProtectedPersons,
  transformProtectedPersonToBackend,
} from '@/utils/dataTransform';

// Extend the base type with additional UI-specific fields
export interface ProtectedPerson extends BaseProtectedPerson {
  department?: string;
  status?: 'active' | 'inactive' | 'travel';
  region?: string;
  securityDetail?: string;
  notes?: string;
  lastUpdate?: string;
}

export interface MedicalAttachment {
  id: string;
  attachmentType: 'INSURANCE_CARD_FRONT' | 'INSURANCE_CARD_BACK' | 'CONSENT_FORM' | 'OTHER';
  url: string;
  filename: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  virusScanStatus: string;
  originalFilename?: string;
  createdAt: string;
}

export interface CustomField {
  name: string;
  value: string;
  type: 'text' | 'number' | 'date' | 'select' | 'textarea';
  options?: string[];
}

export interface ActivityAttachment {
  id: string;
  activityId: string;
  filename: string;
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  filePath: string;
  uploadedBy: string;
  uploadedAt: string;
  virusScanStatus: 'PENDING' | 'CLEAN' | 'INFECTED';
  deletedAt?: string;
}

export interface ActivityAgentAssignment {
  userId: string;
  name: string | null;
  role: string | null;
  isLead: boolean;
}

export interface Activity {
  id: string;
  protectedPersonId: string; // Primary protected person
  protectedPersonIds?: string[]; // Array of all protected person IDs
  protectedPersons?: ProtectedPerson[]; // Populated array of protected persons
  protectedPersonName?: string;
  activityType?: 'TRAVEL' | 'DINING' | 'EVENT' | 'MEETING' | 'OTHER';
  // Legacy field for backwards compatibility
  type?: 'meeting' | 'travel' | 'event' | 'other';
  title: string;
  description?: string;
  location?: string;
  locationLatitude?: number;
  locationLongitude?: number;
  // Backend uses these field names
  startDatetime?: string;
  endDatetime?: string;
  // Legacy fields for backwards compatibility
  startTime?: string;
  endTime?: string;
  status: 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'DEBRIEFING' | 'CLOSED';
  venue?: string;
  venueId?: string;
  attendees?: number;
  securityLevel?: string;
  riskAssessment?: string;
  securityRequirements?: string;
  // Medical information
  closestHospitalName?: string;
  closestHospitalAddress?: string;
  closestHospitalPhone?: string;
  closestHospitalDistance?: string;
  closestHospitalTravelTime?: string;
  closestHospitalCapabilities?: string;
  alternativeMedicalFacilities?: string;
  evacuationRoute?: string;
  onsiteMedicalSupport?: string;
  // After Action Report fields (legacy)
  aarSummary?: string;
  aarTimeline?: string;
  aarRecommendations?: string;
  // After Action Report fields (new structured)
  aarPositives?: string;
  aarNegatives?: string;
  // AAR Signature fields
  aarPreparedByName?: string;
  aarPreparedByTitle?: string;
  aarPreparedAt?: string;
  aarApprovedByName?: string;
  aarApprovedByTitle?: string;
  aarApprovedAt?: string;
  // AAR metadata
  aarStartedAt?: string;
  aarStartedBy?: string;
  aarCompletedAt?: string;
  aarCompletedBy?: string;
  // Security agent assignments (VIS-135)
  agentIds?: string[];
  leadAgentId?: string | null;
  agents?: ActivityAgentAssignment[];
  customFields?: CustomField[];
  communicationPlan?: CommunicationPlan | null;
  attachments?: ActivityAttachment[];
  correctiveActions?: CorrectiveAction[];
  createdBy?: string;
  createdAt: string;
  updatedAt?: string;
  // Control flag (not persisted): explicit confirmation to create/update an
  // activity whose end time is already in the past (VIS-136)
  allowPastEnd?: boolean;
}

export interface CorrectiveAction {
  id: string;
  activityId: string;
  description: string;
  assignedTo?: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  dueDate?: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCorrectiveActionData {
  description: string;
  assignedTo?: string;
  priority?: 'HIGH' | 'MEDIUM' | 'LOW';
  dueDate?: string;
  status?: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
}

export interface UpdateCorrectiveActionData {
  description?: string;
  assignedTo?: string;
  priority?: 'HIGH' | 'MEDIUM' | 'LOW';
  dueDate?: string;
  status?: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
}

export interface Geofence {
  id: string;
  name: string;
  fenceType: 'SAFE_ZONE' | 'RESTRICTED_ZONE' | 'ALERT_ZONE';
  centerLatitude: number | string; // API may return as string
  centerLongitude: number | string; // API may return as string
  radiusMeters: number;
  description?: string;
  isActive: boolean;
  alertOnEntry?: boolean;
  alertOnExit?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ActivityConflict {
  type: 'time_overlap' | 'resource_conflict' | 'location_conflict';
  severity: 'warning' | 'error';
  conflictingActivityId: string;
  conflictingActivity?: Activity;
  message: string;
  startTime: string;
  endTime: string;
  // Backend MissionConflict fields (used by agent double-booking warnings)
  conflictType?: string;
  description?: string;
  conflictingMissionTitle?: string;
  conflictingMissionStart?: string;
  conflictingMissionEnd?: string;
  agentId?: string;
}

export interface ConflictCheckResult {
  hasConflicts: boolean;
  conflicts: ActivityConflict[];
}

export interface MissionWithConflicts {
  activity: Activity;
  conflicts: ActivityConflict[];
}

export interface GeofenceAlert {
  id: string;
  geofenceId: string;
  geofenceName: string;
  type: 'entry' | 'exit' | 'proximity';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  timestamp: string;
  location?: {
    lat: number;
    lng: number;
  };
  acknowledged?: boolean;
}

export interface Venue {
  id: string;
  name: string;
  address: string;
  securityContactName?: string;
  securityContactPhone?: string;
  city: string;
  state: string;
  country?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface ExecutiveProtectionStats {
  total: number;
  active: number;
  onAssignment: number;
  highRisk: number;
  byRiskLevel?: Record<string, number>;
  byRegion?: Record<string, number>;
  activeThreats?: number;
  upcomingTravels?: number;
}

/** Structured error thrown by activity create/update so callers can react to specific codes (VIS-136). */
export class ActivityApiError extends Error {
  code?: string;
  details?: unknown;

  constructor(message: string, code?: string, details?: unknown) {
    super(message);
    this.name = 'ActivityApiError';
    this.code = code;
    this.details = details;
  }
}

function toActivityApiError(error: unknown, fallbackMessage: string): ActivityApiError {
  if (typeof error === 'string') {
    return new ActivityApiError(error);
  }
  const structured = error as { code?: string; message?: string; details?: unknown } | undefined;
  return new ActivityApiError(structured?.message ?? fallbackMessage, structured?.code, structured?.details);
}

/**
 * Normalize activity start/end to UTC ISO before sending. The form emits
 * datetime-local wall-time strings; the API stores real UTC instants. Without
 * this the wall time is stamped as UTC and re-rendered browser-local, shifting
 * every scheduled time by the browser's UTC offset. Idempotent for values that
 * already carry a timezone.
 */
function withUtcDatetimes<T extends { startDatetime?: unknown; endDatetime?: unknown }>(data: T): T {
  const next = { ...data };
  if ('startDatetime' in next) {
    next.startDatetime = toUtcIso(next.startDatetime as string | Date | undefined | null);
  }
  if ('endDatetime' in next) {
    next.endDatetime = toUtcIso(next.endDatetime as string | Date | undefined | null);
  }
  return next;
}

class ExecutiveProtectionService {
  /**
   * Get all protected persons
   */
  async getProtectedPersons(params?: { page?: number; limit?: number }): Promise<{
    protectedPersons: ProtectedPerson[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) {
      searchParams.set('page', String(params.page));
    }
    if (params?.limit) {
      searchParams.set('limit', String(params.limit));
    }
    const queryString = searchParams.toString();
    const url = `/api/v1/executive-protection${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<{
      protectedPersons: BackendProtectedPerson[];
      pagination: { total: number; page: number; limit: number; totalPages: number };
    }>(url);

    if (!response.success || !response.data) {
      return { protectedPersons: [], pagination: { total: 0, page: 1, limit: 10, totalPages: 0 } };
    }

    return {
      protectedPersons: transformProtectedPersons(response.data.protectedPersons),
      pagination: response.data.pagination,
    };
  }

  /**
   * Get protected person by ID
   */
  async getProtectedPersonById(id: string): Promise<ProtectedPerson | null> {
    const response = await apiClient.get<BackendProtectedPerson>(`/api/v1/executive-protection/${id}`);
    if (!response.success || !response.data) { return null; }
    return transformProtectedPerson(response.data);
  }

  /**
   * Create protected person
   */
  async createProtectedPerson(data: Omit<ProtectedPerson, 'id' | 'createdAt' | 'lastUpdate'>): Promise<ProtectedPerson | null> {
    const backendData = transformProtectedPersonToBackend(data);
    const response = await apiClient.post<BackendProtectedPerson>('/api/v1/executive-protection', backendData);
    if (!response.success || !response.data) { return null; }
    return transformProtectedPerson(response.data);
  }

  /**
   * Update protected person
   */
  async updateProtectedPerson(id: string, data: Partial<ProtectedPerson>): Promise<ProtectedPerson | null> {
    const backendData = transformProtectedPersonToBackend(data);
    const response = await apiClient.put<BackendProtectedPerson>(`/api/v1/executive-protection/${id}`, backendData);
    if (!response.success || !response.data) { return null; }
    return transformProtectedPerson(response.data);
  }

  /**
   * Delete protected person
   */
  async deleteProtectedPerson(id: string): Promise<boolean> {
    const response = await apiClient.delete(`/api/v1/executive-protection/${id}`);
    return response.success;
  }

  /**
   * Get all missions
   */
  async getActivities(): Promise<Activity[]> {
    const response = await apiClient.get<Activity[]>('/api/v1/executive-protection/activities');
    return response.success && response.data ? response.data : [];
  }

  /**
   * Get today's activities (PLANNED or IN_PROGRESS with startDatetime = today)
   */
  async getTodaysActivities(): Promise<Activity[]> {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const params = new URLSearchParams();
    params.append('startDate', startOfDay.toISOString());
    params.append('endDate', endOfDay.toISOString());

    const response = await apiClient.get<Activity[]>(`/api/v1/executive-protection/activities?${params.toString()}`);
    if (!response.success || !response.data) {
      return [];
    }

    // Filter to only show PLANNED or IN_PROGRESS activities
    return response.data.filter(activity => {
      const status = activity.status.toUpperCase();
      return status === 'PLANNED' || status === 'IN_PROGRESS' || status === 'SCHEDULED';
    });
  }

  /**
   * Get filtered activities with status and date range filters
   */
  async getFilteredActivities(filters: {
    status?: 'ALL' | 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
    dateRange?: 'TODAY' | 'UPCOMING' | 'PAST' | 'ALL';
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<Activity[]> {
    const params = new URLSearchParams();

    // Status filter
    if (filters.status && filters.status !== 'ALL') {
      params.append('status', filters.status);
    }

    // Date range filter
    const now = new Date();
    if (filters.dateRange === 'TODAY') {
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      params.append('startDate', startOfDay.toISOString());
      params.append('endDate', endOfDay.toISOString());
    } else if (filters.dateRange === 'UPCOMING') {
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      const futureDate = new Date(now);
      futureDate.setDate(futureDate.getDate() + 30);
      params.append('startDate', startOfDay.toISOString());
      params.append('endDate', futureDate.toISOString());
    } else if (filters.dateRange === 'PAST') {
      const pastDate = new Date(now);
      pastDate.setDate(pastDate.getDate() - 30);
      const endOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
      params.append('startDate', pastDate.toISOString());
      params.append('endDate', endOfYesterday.toISOString());
    }
    // 'ALL' = no date filter

    if (filters.search) {
      params.append('search', filters.search);
    }
    if (filters.page) {
      params.append('page', filters.page.toString());
    }
    if (filters.limit) {
      params.append('limit', filters.limit.toString());
    }

    const queryString = params.toString();
    const url = queryString
      ? `/api/v1/executive-protection/activities?${queryString}`
      : '/api/v1/executive-protection/activities';

    const response = await apiClient.get<Activity[]>(url);
    return response.success && response.data ? response.data : [];
  }

  /**
   * Get activity by ID
   */
  async getActivityById(id: string): Promise<Activity | null> {
    const response = await apiClient.get<Activity>(`/api/v1/executive-protection/activities/${id}`);
    if (!response.success) {
      console.error('[EP Service] getActivityById failed', {
        id,
        errorCode: (response.error as { code?: string })?.code,
        errorMessage: (response.error as { message?: string })?.message ?? response.error,
      });
    }
    return response.success && response.data ? response.data : null;
  }

  /**
   * Create mission
   * @throws {ActivityApiError} when the API rejects the request (e.g. END_IN_PAST)
   */
  async createActivity(data: Omit<Activity, 'id' | 'createdAt' | 'updatedAt'>): Promise<Activity | null> {
    const response = await apiClient.post<Activity>(
      '/api/v1/executive-protection/activities',
      withUtcDatetimes(data)
    );
    if (!response.success) {
      throw toActivityApiError(response.error, 'Failed to create activity');
    }
    return response.data ?? null;
  }

  /**
   * Update mission
   * @throws {ActivityApiError} when the API rejects the request (e.g. END_IN_PAST, invalid transition)
   */
  async updateActivity(id: string, data: Partial<Activity>): Promise<Activity | null> {
    const response = await apiClient.put<Activity>(
      `/api/v1/executive-protection/activities/${id}`,
      withUtcDatetimes(data)
    );
    if (!response.success) {
      throw toActivityApiError(response.error, 'Failed to update activity');
    }
    return response.data ?? null;
  }

  async startDebrief(activityId: string): Promise<Activity | null> {
    const response = await apiClient.put<Activity>(
      `/api/v1/executive-protection/activities/${activityId}`,
      { status: 'DEBRIEFING' }
    );
    return response.success && response.data ? response.data : null;
  }

  async saveAarDraft(activityId: string, data: {
    aarSummary?: string;
    aarTimeline?: string;
    aarRecommendations?: string;
    aarPositives?: string;
    aarNegatives?: string;
    aarPreparedByName?: string;
    aarPreparedByTitle?: string;
    aarPreparedAt?: string;
    aarApprovedByName?: string;
    aarApprovedByTitle?: string;
    aarApprovedAt?: string;
  }): Promise<Activity | null> {
    const response = await apiClient.put<Activity>(
      `/api/v1/executive-protection/activities/${activityId}`,
      data
    );
    return response.success && response.data ? response.data : null;
  }

  async completeAar(activityId: string, data: {
    aarSummary?: string;
    aarTimeline?: string;
    aarRecommendations?: string;
    aarPositives?: string;
    aarNegatives?: string;
    aarPreparedByName?: string;
    aarPreparedByTitle?: string;
    aarPreparedAt?: string;
    aarApprovedByName?: string;
    aarApprovedByTitle?: string;
    aarApprovedAt?: string;
  }): Promise<Activity | null> {
    const response = await apiClient.put<Activity>(
      `/api/v1/executive-protection/activities/${activityId}`,
      { ...data, status: 'CLOSED' }
    );
    return response.success && response.data ? response.data : null;
  }

  // Corrective Actions CRUD
  async getCorrectiveActions(activityId: string): Promise<CorrectiveAction[]> {
    const response = await apiClient.get<CorrectiveAction[]>(
      `/api/v1/executive-protection/activities/${activityId}/corrective-actions`
    );
    return response.success && response.data ? response.data : [];
  }

  async createCorrectiveAction(activityId: string, data: CreateCorrectiveActionData): Promise<CorrectiveAction | null> {
    const response = await apiClient.post<CorrectiveAction>(
      `/api/v1/executive-protection/activities/${activityId}/corrective-actions`,
      data
    );
    return response.success && response.data ? response.data : null;
  }

  async updateCorrectiveAction(activityId: string, actionId: string, data: UpdateCorrectiveActionData): Promise<CorrectiveAction | null> {
    const response = await apiClient.put<CorrectiveAction>(
      `/api/v1/executive-protection/activities/${activityId}/corrective-actions/${actionId}`,
      data
    );
    return response.success && response.data ? response.data : null;
  }

  async deleteCorrectiveAction(activityId: string, actionId: string): Promise<boolean> {
    const response = await apiClient.delete(
      `/api/v1/executive-protection/activities/${activityId}/corrective-actions/${actionId}`
    );
    return response.success;
  }

  async revertDebrief(activityId: string): Promise<Activity | null> {
    const response = await apiClient.put<Activity>(
      `/api/v1/executive-protection/activities/${activityId}`,
      { status: 'COMPLETED' }
    );
    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete mission
   */
  async deleteActivity(id: string): Promise<boolean> {
    const response = await apiClient.delete(`/api/v1/executive-protection/activities/${id}`);
    return response.success;
  }

  /**
   * Get all geofences
   */
  async getGeofences(): Promise<Geofence[]> {
    const response = await apiClient.get<Geofence[]>('/api/v1/executive-protection/geofences');
    return response.success && response.data ? response.data : [];
  }

  /**
   * Create geofence
   */
  async createGeofence(data: Omit<Geofence, 'id' | 'createdAt'>): Promise<Geofence | null> {
    const response = await apiClient.post<Geofence>('/api/v1/executive-protection/geofences', data);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Update geofence
   */
  async updateGeofence(id: string, data: Partial<Geofence>): Promise<Geofence | null> {
    const response = await apiClient.put<Geofence>(`/api/v1/executive-protection/geofences/${id}`, data);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Delete geofence
   */
  async deleteGeofence(id: string): Promise<boolean> {
    const response = await apiClient.delete(`/api/v1/executive-protection/geofences/${id}`);
    return response.success;
  }

  /**
   * Get all alerts
   */
  async getAlerts(): Promise<GeofenceAlert[]> {
    const response = await apiClient.get<GeofenceAlert[]>('/api/v1/executive-protection/alerts');
    return response.success && response.data ? response.data : [];
  }

  /**
   * Get all venues
   */
  async getVenues(): Promise<Venue[]> {
    const response = await apiClient.get<Venue[]>('/api/v1/executive-protection/venues');
    return response.success && response.data ? response.data : [];
  }

  /**
   * Create a new venue
   */
  async createVenue(data: { name: string; address: string }): Promise<Venue | null> {
    const response = await apiClient.post<Venue>('/api/v1/executive-protection/venues', data);
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get executive protection statistics
   */
  async getStats(): Promise<ExecutiveProtectionStats | null> {
    const response = await apiClient.get<ExecutiveProtectionStats>('/api/v1/executive-protection/stats');
    return response.success && response.data ? response.data : null;
  }

  /**
   * Upload profile picture for a protected person
   */
  async uploadProfilePicture(id: string, file: File, onProgress?: (progress: number) => void): Promise<string | null> {
    const response = await apiClient.uploadFile<{ profilePictureUrl: string }>(
      `/api/v1/executive-protection/${id}/profile-picture`,
      file,
      onProgress
    );
    return response.success && response.data ? response.data.profilePictureUrl : null;
  }

  /**
   * Upload medical attachment for a protected person
   */
  async uploadMedicalAttachment(
    id: string,
    file: File,
    attachmentType: 'INSURANCE_CARD_FRONT' | 'INSURANCE_CARD_BACK' | 'CONSENT_FORM' | 'OTHER',
    onProgress?: (progress: number) => void
  ): Promise<MedicalAttachment | null> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('attachmentType', attachmentType);

    const response = await apiClient.uploadFile<MedicalAttachment>(
      `/api/v1/executive-protection/${id}/medical-attachments`,
      file,
      onProgress,
      { attachmentType } // Pass attachmentType as additional data
    );
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get medical attachments for a protected person
   */
  async getMedicalAttachments(id: string): Promise<MedicalAttachment[]> {
    const response = await apiClient.get<MedicalAttachment[]>(`/api/v1/executive-protection/${id}/medical-attachments`);
    return response.success && response.data ? response.data : [];
  }

  /**
   * Delete medical attachment
   */
  async deleteMedicalAttachment(personId: string, attachmentId: string): Promise<boolean> {
    const response = await apiClient.delete(`/api/v1/executive-protection/${personId}/medical-attachments/${attachmentId}`);
    return response.success;
  }

  /**
   * Upload an attachment to a mission
   */
  async uploadActivityAttachment(activityId: string, file: File, onProgress?: (progress: number) => void): Promise<ActivityAttachment | null> {
    const response = await apiClient.uploadFile<ActivityAttachment>(
      `/api/v1/executive-protection/activities/${activityId}/attachments`,
      file,
      onProgress
    );
    if (!response.success) {
      console.error('[EP Service] uploadActivityAttachment failed', {
        activityId,
        fileName: file.name,
        fileSize: file.size,
        errorCode: (response.error as { code?: string })?.code,
        errorMessage: (response.error as { message?: string })?.message ?? response.error,
      });
    }
    return response.success && response.data ? response.data : null;
  }

  /**
   * Get all attachments for a mission
   */
  async getActivityAttachments(activityId: string): Promise<ActivityAttachment[]> {
    const response = await apiClient.get<ActivityAttachment[]>(
      `/api/v1/executive-protection/activities/${activityId}/attachments`
    );
    return response.success && response.data ? response.data : [];
  }

  /**
   * Download a activity attachment
   */
  async downloadActivityAttachment(attachmentId: string, filename?: string): Promise<void> {
    const url = `/api/v1/executive-protection/activities/attachments/${attachmentId}/download`;
    await apiClient.downloadFile(url, filename);
  }

  /**
   * Delete a activity attachment
   */
  async deleteActivityAttachment(attachmentId: string): Promise<boolean> {
    const response = await apiClient.delete(
      `/api/v1/executive-protection/activities/attachments/${attachmentId}`
    );
    return response.success;
  }

  // Conflict Detection methods
  /**
   * Check for activity conflicts
   */
  async checkActivityConflicts(params: {
    protectedPersonId?: string;
    agentIds?: string[];
    startDatetime: string;
    endDatetime?: string;
    excludeActivityId?: string;
  }): Promise<ConflictCheckResult> {
    const response = await apiClient.post<ConflictCheckResult>(
      '/api/v1/executive-protection/activities/conflicts/check',
      {
        protectedPersonId: params.protectedPersonId,
        agentIds: params.agentIds,
        startDatetime: params.startDatetime,
        endDatetime: params.endDatetime,
        // Backend expects excludeMissionId
        excludeMissionId: params.excludeActivityId,
      }
    );
    return response.success && response.data ? response.data : { hasConflicts: false, conflicts: [] };
  }

  /**
   * Get conflicts for a specific mission
   */
  async getActivityConflicts(activityId: string): Promise<ConflictCheckResult> {
    const response = await apiClient.get<ConflictCheckResult>(
      `/api/v1/executive-protection/activities/${activityId}/conflicts`
    );
    return response.success && response.data ? response.data : { hasConflicts: false, conflicts: [] };
  }

  /**
   * Get conflicts in a date range for a protected person
   */
  async getConflictsInDateRange(
    protectedPersonId: string,
    startDate: string,
    endDate: string
  ): Promise<MissionWithConflicts[]> {
    const response = await apiClient.get<MissionWithConflicts[]>(
      `/api/v1/executive-protection/activities/conflicts/person/${protectedPersonId}/range?startDate=${startDate}&endDate=${endDate}`
    );
    return response.success && response.data ? response.data : [];
  }

  // Recurring Activity methods
  /**
   * Process a recurring activity (generate all instances)
   */
  async processRecurringActivity(activityId: string): Promise<{ count: number; activityIds: string[] }> {
    const response = await apiClient.post<{ count: number; activityIds: string[] }>(
      `/api/v1/executive-protection/activities/${activityId}/recurring/process`,
      {}
    );
    return response.success && response.data ? response.data : { count: 0, activityIds: [] };
  }

  /**
   * Get recurring instances for a parent mission
   */
  async getRecurringInstances(activityId: string): Promise<Activity[]> {
    const response = await apiClient.get<Activity[]>(
      `/api/v1/executive-protection/activities/${activityId}/recurring/instances`
    );
    return response.success && response.data ? response.data : [];
  }

  /**
   * Update recurring instances
   */
  async updateRecurringInstances(activityId: string, updates: Partial<Activity>): Promise<{ count: number }> {
    const response = await apiClient.put<{ count: number }>(
      `/api/v1/executive-protection/activities/${activityId}/recurring/instances`,
      updates
    );
    return response.success && response.data ? response.data : { count: 0 };
  }

  /**
   * Delete recurring instances
   */
  async deleteRecurringInstances(activityId: string): Promise<{ count: number }> {
    const response = await apiClient.delete<{ count: number }>(
      `/api/v1/executive-protection/activities/${activityId}/recurring/instances`
    );
    return response.success && response.data ? response.data : { count: 0 };
  }
}

export const executiveProtectionService = new ExecutiveProtectionService();
